import React, {useContext} from "react";
import {
  Box, CircularProgress, FormControl, FormControlLabel, Grid, IconButton,
  makeStyles, MenuItem, Radio, RadioGroup, Typography,
} from "@material-ui/core";
import {FormattedMessage, useIntl} from "react-intl";
import CloseIcon from "@material-ui/icons/Close";
import * as Yup from "yup";
import {FormikHelpers, useFormik} from "formik";

import {DefaultButton, OutlineButton} from "./Buttons";
import {AppScrollbar} from "../../../components/scrollbar/AppScrollbar";
import {InputSelect} from "../../../components/input-select/InputSelect";
import {MemberStatus} from "../../../settings/MemberStatus";
import {NotificationWrapper} from "./GenericWrapper";
import {NotificationDispatchContext} from "../../../context/NotificationContext";
import {FilterState} from "../../../attributes/filters-state";
import {InputField} from "../../../components/input-field/InputField";

export type SortableValue = 'desc' | 'asc';

export interface PersonalAccountFiltersProps {
  filters: FilterState;
  onClose: () => void;
  updateFilters: (filters: FilterState) => Promise<boolean>;
}

export const useStyles = makeStyles(
  (theme) => ({
    root: {
      overflow: "hidden"
    },
    headerWrapper: {},
    header: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(2),
    },
    footerWrapper: {
      display: "flex",
      alignItems: "center",
      height: "80px",

      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(4),
      borderTop: "1px solid #e8e8e8"
    },
    footerExtra: {
      flexGrow: 1,
      textAlign: "start",
    },
    footerActions: {
      display: "flex",
      alignItems: "center",
    },
    contentWrapper: {
      height: "calc(100vh - 150px)",
    },
    filterItem: {
      padding: "4px 26px"
    },
    filterItemTitle: {
      textAlign: "left",
      fontSize: "0.88rem",
      color: theme.palette.grey[600]
    },
    filterItemContent: {},
    caption: {
      textAlign: "left",
      fontSize: "0.86rem",
      color: theme.palette.grey[500],
      paddingTop: "10px"
    },
  }),
  {
    name: "filters-wrapper-style",
    flip: null,
  }
);

const PersonalAccountFilters = (props: PersonalAccountFiltersProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  const {onClose, updateFilters, filters} = props;
  // Context
  const dispatch = useContext(NotificationDispatchContext);

  const formik = useFormik({
    initialValues: filters,
    validationSchema: Yup.object().shape({}),
    onSubmit: async (values: any, { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      let result = await updateFilters(values);

      if(result) {
        onClose();
        return;
      }

      dispatch({
        data: {
          type: "error",
          message: intl.formatMessage({ id: "sharedScreen.unableToLoadData" }),
        }
      });
    },
  });

  return (
      <Box className={classes.root}>
        <NotificationWrapper />
        <form noValidate autoComplete="off">
          <Box component="div" className={classes.headerWrapper}>
            <Box component="div" className={classes.header}>
              <Box display="flex" alignItems="center">
                <Box style={{ flexGrow: 1 }}>
                  <Typography component="span" style={{fontWeight: 600, fontSize: "1.2rem"}}>
                    <FormattedMessage id="sharedScreen.addFilters" />
                  </Typography>
                </Box>
                <IconButton href="" aria-label="close" onClick={(_) => onClose()}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
          <AppScrollbar>
            <Box component="div" className={classes.contentWrapper} pb={6}>
              <Box component="div" mt={2} style={{ paddingBottom: "4rem" }}>
                <Grid container component="div" className={classes.filterItem}>
                  <Grid item sm={12} component="div">
                    <Box component="div">
                      <FormControl component="fieldset">
                        <RadioGroup
                            row
                            aria-label="creation"
                            name="creation_filter"
                            defaultValue={filters.creation_filter}
                            onChange={formik.handleChange}
                        >
                          <FormControlLabel value="asc" control={<Radio size="small" />} label={intl.formatMessage({id: "sharedScreen.mostOld"})} />
                          <FormControlLabel value="desc" control={<Radio size="small" />} label={intl.formatMessage({id: "sharedScreen.mostRecent"})} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container component="div" className={classes.filterItem}>
                  <Grid item sm={12} component="div" className={classes.filterItemContent}>
                    <Box>
                      <Box>
                        <Typography component="p" className={classes.filterItemTitle}>
                          <FormattedMessage id="sharedScreen.status" />
                        </Typography>
                      </Box>
                      <FormControl component="fieldset">
                        <RadioGroup
                            row aria-label="status"
                            name="status_filter"
                            defaultValue={filters.status_filter}
                            onChange={formik.handleChange}
                        >

                          <FormControlLabel value={MemberStatus.all} control={<Radio size="small" />} label={intl.formatMessage({id: "sharedScreen.all"})} />
                          <FormControlLabel value={MemberStatus.pending} control={<Radio size="small" />} label={intl.formatMessage({id: "sharedScreen.pending"})} />
                          <FormControlLabel value={MemberStatus.declined} control={<Radio size="small" />} label={intl.formatMessage({id: "sharedScreen.declined"})} />
                          <FormControlLabel value={MemberStatus.approved} control={<Radio size="small" />} label={intl.formatMessage({id: "sharedScreen.approved"})} />
                          <FormControlLabel value={MemberStatus.new} control={<Radio size="small" />} label={intl.formatMessage({id: "sharedScreen.newest"})} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container component="div" className={classes.filterItem}>
                  <Grid item sm={12} component="div" className={classes.filterItemContent}>
                    <Box mb={1}>
                      <Typography component="p" className={classes.filterItemTitle}>
                        <FormattedMessage id="sharedScreen.advancedSearch" />
                      </Typography>
                    </Box>
                    <Box>
                      <InputField
                          fullWidth
                          id="search_filter"
                          name="search_filter"
                          placeholder={intl.formatMessage({
                            id: "sharedScreen.research",
                          })}
                          type="text"
                          value={formik.values.search_filter}
                          onChange={formik.handleChange}
                      />
                    </Box>
                    <Box mb={2}>
                      <Typography component="p" className={classes.caption}>
                        <FormattedMessage id="sharedScreen.filterPersonalAccountNotice" />
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container component="div" className={classes.filterItem}>
                  <Grid item sm={12} component="div">
                    <Typography component="p" className={classes.filterItemTitle}>
                      <FormattedMessage id="sharedScreen.sortBy" />
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container component="div" className={classes.filterItem}>
                  <Grid item sm={3} component="div">
                    <Box style={{paddingTop: "10px"}}>
                      <Typography component="p" className={classes.filterItemTitle}>
                        <FormattedMessage id="sharedScreen.declinedAt" />
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item sm={9} component="div" className={classes.filterItemContent}>
                    <Box ml={2}>
                      <InputSelect
                          fullWidth
                          id="decline_filter"
                          name="decline_filter"
                          value={formik.values.decline_filter === null ? "0" : formik.values.decline_filter}
                          onChange={formik.handleChange}
                      >
                        <MenuItem value="0" disabled>
                          <FormattedMessage id="sharedScreen.selectValue" />
                        </MenuItem>
                        <MenuItem value="asc">
                          <FormattedMessage id="sharedScreen.ascending" />
                        </MenuItem>
                        <MenuItem value="desc">
                          <FormattedMessage id="sharedScreen.descending" />
                        </MenuItem>
                      </InputSelect>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container component="div" className={classes.filterItem}>
                  <Grid item sm={3} component="div">
                    <Box style={{paddingTop: "10px"}}>
                      <Typography component="p" className={classes.filterItemTitle}>
                        <FormattedMessage id="sharedScreen.approvedAt" />
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item sm={9} component="div" className={classes.filterItemContent}>
                    <Box ml={2}>
                      <InputSelect
                          fullWidth
                          id="approve_filter"
                          name="approve_filter"
                          value={formik.values.approve_filter === null ? "0" : formik.values.approve_filter}
                          onChange={formik.handleChange}
                      >
                        <MenuItem value="0" disabled>
                          <FormattedMessage id="sharedScreen.selectValue" />
                        </MenuItem>
                        <MenuItem value="asc">
                          <FormattedMessage id="sharedScreen.ascending" />
                        </MenuItem>
                        <MenuItem value="desc">
                          <FormattedMessage id="sharedScreen.descending" />
                        </MenuItem>
                      </InputSelect>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </AppScrollbar>
          <Box component="div" className={classes.footerWrapper}>
            <Box component="div" className={classes.footerExtra}>
              {
                formik.isSubmitting ? (
                    <CircularProgress color="secondary" disableShrink={false} size={20} thickness={4} />
                ) : (<></>)
              }
            </Box>
            <Box component="div" className={classes.footerActions}>
              <Box component="div">
                <OutlineButton onClick={(_) => onClose()} disabled={formik.isSubmitting}>
                  <FormattedMessage id="sharedScreen.cancel" />
                </OutlineButton>
              </Box>
              <Box component="div" ml={2}>
                <DefaultButton disabled={formik.isSubmitting} type="button" onClick={formik.handleSubmit}>
                  <FormattedMessage id="sharedScreen.applyFilters" />
                </DefaultButton>
              </Box>
            </Box>
          </Box>
        </form>
      </Box>
  );
};

export default PersonalAccountFilters;